import { useEffect, useState } from "react";
import { getAdminBagTags, shipBagTags, unshipBagTags, shipBagTagCard, logAuditEvent } from "../../../api/api-hook";
import { getAdminSession } from "../../../utils/adminAuth";
import { hubspotRecordUrl } from "../../../utils/hubspotIds";

type BagTag = {
  dealId: string;
  contactId: string;
  firstName: string;
  lastName: string;
  email: string;
  ghinNumber: string;
  vipCardNumber: string;
  bagTagName: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  vipPackage: string;
  dealstage: string;
  amount: string;
  amountRaw: number;
  createDate: string;
  isReplacement: boolean;
  shippedDate: string;
  mailedAt: string;
  shippedCardDate?: string;
};

const BRAND_GREEN = "#023822";
const BRAND_GOLD  = "#C9A24D";

const formatDate = (iso: string) => {
  if (!iso) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
  }
  const d = /^\d{10,}$/.test(iso) ? new Date(Number(iso)) : new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
};

const BulkBagTags = () => {
  const [bagTags, setBagTags]         = useState<BagTag[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [search, setSearch]           = useState("");
  const [pageSize, setPageSize]       = useState<20 | 50 | 100>(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [showShipped, setShowShipped] = useState(false);
  const [selected, setSelected]       = useState<Set<string>>(new Set());
  const [shipping, setShipping]       = useState(false);
  const [shipError, setShipError]     = useState<string | null>(null);
  const [unshipping, setUnshipping]   = useState(false);
  const [unshipError, setUnshipError] = useState<string | null>(null);
  const [openPickerFor, setOpenPickerFor] = useState<string | null>(null);
  const [pickDate, setPickDate]           = useState("");
  const [shipping2, setShipping2]         = useState(false);
  const [bulkShipDate, setBulkShipDate]     = useState(new Date().toISOString().split("T")[0]);
  const [bulkApplying, setBulkApplying]     = useState(false);

  const fetchBagTags = async (mailed = false) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getAdminBagTags(mailed);
      if (resp.success) {
        setBagTags(resp.bagTags);
        setSelected(new Set());
      } else {
        setError(resp.message || "Failed to load bag tags.");
        setBagTags([]);
      }
    } catch {
      setError("Network error. Please try again.");
      setBagTags([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBagTags(false); }, []);
  useEffect(() => { setCurrentPage(1); setSelected(new Set()); fetchBagTags(showShipped); }, [showShipped]);
  useEffect(() => { setCurrentPage(1); setSelected(new Set()); }, [search, pageSize]);

  const filtered = bagTags.filter((t) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (t.firstName || "").toLowerCase().includes(q) ||
      (t.lastName || "").toLowerCase().includes(q) ||
      (t.email || "").toLowerCase().includes(q) ||
      (t.bagTagName || "").toLowerCase().includes(q) ||
      (t.ghinNumber || "").toLowerCase().includes(q) ||
      (t.vipCardNumber || "").toLowerCase().includes(q) ||
      (t.vipPackage || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(currentPage, totalPages);
  const paginated  = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const pageIds      = paginated.map((t) => t.dealId);
  const allPageSel   = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const somePageSel  = pageIds.some((id) => selected.has(id));

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSel) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleExportAndShip = async () => {
    if (selected.size === 0) return;
    setShipping(true);
    setShipError(null);
    try {
      const dealIds = Array.from(selected);
      const toShip  = bagTags.filter((t) => selected.has(t.dealId));

      // CSV export
      const headers = ["Name","Email","VIP Card #","Street Address","Address 2","City","State","Zip","Bag Tag Name"];
      const rows    = toShip.map((t) => [
        `${t.firstName} ${t.lastName}`.trim(),
        t.email, t.vipCardNumber,
        t.address, t.address2, t.city, t.state, t.zip,
        t.bagTagName,
      ]);
      const csv = [headers, ...rows]
        .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `bag-tags-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      // Mark as shipped in HubSpot
      const resp = await shipBagTags(dealIds);
      if (!resp.success) throw new Error(resp.message || "Failed to mark as shipped.");

      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "bulk_action",
        label:   "Bag Tags Shipped",
        details: `${dealIds.length} bag tag${dealIds.length !== 1 ? "s" : ""} marked as shipped`,
      });
      // Update local state — mark shipped so they drop out of the unshipped view
      setBagTags((prev) =>
        prev.map((t) => selected.has(t.dealId) ? { ...t, shippedDate: "Yes", mailedAt: new Date().toISOString() } : t)
      );
      setSelected(new Set());
    } catch (err: any) {
      setShipError(err?.response?.data?.message || err?.message || "Failed to export and ship.");
    } finally {
      setShipping(false);
    }
  };

  const handleShipCard = async (t: BagTag, date: string) => {
    setShipping2(true);
    try {
      const [y, m, d] = date.split("-").map(Number);
      const now = new Date();
      const localEpoch = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds()).getTime();
      const resp = await shipBagTagCard(t.dealId, localEpoch);
      if (!resp.success) throw new Error(resp.message || "Failed to update.");
      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "bulk_action",
        label:   "Bag Tag Shipped",
        details: `Bag tag marked as shipped for ${t.firstName} ${t.lastName} (${t.email}) on ${date}`,
      });
      setBagTags((prev) =>
        prev.map((b) =>
          b.dealId === t.dealId ? { ...b, shippedDate: "Yes", shippedCardDate: String(localEpoch) } : b
        )
      );
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || "Failed to update ship date.");
    } finally {
      setShipping2(false);
    }
  };

  const handleBulkShipCard = async (date: string) => {
    const qualifying = filtered.filter((t) => selected.has(t.dealId) && !t.shippedCardDate);
    if (qualifying.length === 0) return;
    setBulkApplying(true);
    try {
      const [y, m, d] = date.split("-").map(Number);
      const now = new Date();
      const localEpoch = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds()).getTime();
      await Promise.all(qualifying.map((t) => shipBagTagCard(t.dealId, localEpoch)));
      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "bulk_action",
        label:   "Bag Tags Ship Date Applied (Bulk)",
        details: `Ship date ${date} applied to ${qualifying.length} bag tag${qualifying.length !== 1 ? "s" : ""}`,
      });
      const updatedIds = new Set(qualifying.map((t) => t.dealId));
      setBagTags((prev) =>
        prev.map((t) =>
          updatedIds.has(t.dealId) ? { ...t, shippedCardDate: String(localEpoch) } : t
        )
      );
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || "Failed to apply ship dates.");
    } finally {
      setBulkApplying(false);
    }
  };

  const handleUnship = async () => {
    if (selected.size === 0) return;
    setUnshipping(true);
    setUnshipError(null);
    try {
      const dealIds = Array.from(selected);
      const resp = await unshipBagTags(dealIds);
      if (!resp.success) throw new Error(resp.message || "Failed to unship.");
      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "bulk_action",
        label:   "Bag Tags Unshipped",
        details: `${dealIds.length} bag tag${dealIds.length !== 1 ? "s" : ""} removed from shipped`,
      });
      setBagTags((prev) => prev.filter((t) => !selected.has(t.dealId)));
      setSelected(new Set());
    } catch (err: any) {
      setUnshipError(err?.response?.data?.message || err?.message || "Failed to unship.");
    } finally {
      setUnshipping(false);
    }
  };

  const thStyle: React.CSSProperties = {
    padding: "0.7rem 0.75rem",
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#6c757d",
    borderBottom: "2px solid #e9ecef",
    whiteSpace: "nowrap",
    background: "#f8f9fa",
  };

  const tdStyle: React.CSSProperties = {
    padding: "0.65rem 0.75rem",
    fontSize: "0.875rem",
    verticalAlign: "middle",
    borderBottom: "1px solid #f0f0f0",
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
        <div>
          <h4 className="mb-1 fw-bold" style={{ color: "#023822", letterSpacing: "-0.3px" }}>Bag Tags</h4>
          <p className="mb-0" style={{ fontSize: "0.82rem", color: "#8a9199" }}>
            <span style={{ fontWeight: 600, color: "#495057" }}>{filtered.length}</span> result{filtered.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
          {selected.size > 0 && (
            <span style={{ fontSize: "0.82rem", color: "#6c757d", whiteSpace: "nowrap" }}>
              {selected.size} selected
            </span>
          )}
          {showShipped ? (
            <>
              <button
                onClick={() => {
                  const allFilteredIds = filtered.map((t) => t.dealId);
                  const allSel = allFilteredIds.every((id) => selected.has(id));
                  setSelected(allSel ? new Set() : new Set(allFilteredIds));
                }}
                style={{
                  background: "none",
                  border: "1px solid #ced4da",
                  borderRadius: 7,
                  color: "#495057",
                  cursor: "pointer",
                  padding: "0.3rem 0.75rem",
                  fontSize: "0.82rem",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {filtered.every((t) => selected.has(t.dealId)) ? "Deselect All" : `Select All (${filtered.length})`}
              </button>
              <input
                type="date"
                value={bulkShipDate}
                onChange={(e) => setBulkShipDate(e.target.value)}
                style={{ fontSize: "0.82rem", padding: "0.3rem 0.5rem", borderRadius: 7, border: "1px solid #ced4da", outline: "none" }}
              />
              <button
                onClick={() => handleBulkShipCard(bulkShipDate)}
                disabled={!bulkShipDate || selected.size === 0 || bulkApplying}
                style={{
                  backgroundColor: (bulkShipDate && selected.size > 0) ? "#023822" : "#e9ecef",
                  color: (bulkShipDate && selected.size > 0) ? "#fff" : "#adb5bd",
                  border: "none",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  borderRadius: 8,
                  padding: "0.35rem 1rem",
                  whiteSpace: "nowrap",
                }}
              >
                {bulkApplying ? "Applying…" : "Apply Ship Date"}
              </button>
              <button
                onClick={handleUnship}
                disabled={selected.size === 0 || unshipping}
                style={{ backgroundColor: selected.size > 0 ? "#dc3545" : "#e9ecef", color: selected.size > 0 ? "#fff" : "#adb5bd", border: "none", fontWeight: 600, fontSize: "0.82rem", borderRadius: 8, padding: "0.35rem 1rem" }}
              >
                {unshipping ? "Processing…" : "Unship"}
              </button>
            </>
          ) : (
            <button
              onClick={handleExportAndShip}
              disabled={selected.size === 0 || shipping}
              style={{ backgroundColor: selected.size > 0 ? BRAND_GOLD : "#e9ecef", color: selected.size > 0 ? "#fff" : "#adb5bd", border: "none", fontWeight: 600, fontSize: "0.82rem", borderRadius: 8, padding: "0.35rem 1rem" }}
            >
              {shipping ? "Processing…" : "Export & Ship"}
            </button>
          )}
        </div>
      </div>

      {error       && <div className="alert alert-danger small">{error}</div>}
      {shipError   && <div className="alert alert-danger small">{shipError}</div>}
      {unshipError && <div className="alert alert-danger small">{unshipError}</div>}

      {/* Controls */}
      <div style={{ background: "#f8f9fa", borderRadius: 12, padding: "0.85rem 1rem", marginBottom: "1.25rem", border: "1px solid #e9ecef" }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search name, email, GHIN, card number, bag tag name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 340, borderRadius: 8 }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.82rem", color: "#6c757d", cursor: "pointer", userSelect: "none" }}>
            <input
              type="checkbox"
              checked={showShipped}
              onChange={(e) => setShowShipped(e.target.checked)}
            />
            Show shipped
          </label>
        </div>
      </div>

      {/* Table */}
      <div style={{ borderRadius: 12, border: "1px solid #e9ecef", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: 40, textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={allPageSel}
                  ref={(el) => { if (el) el.indeterminate = somePageSel && !allPageSel; }}
                  onChange={toggleSelectAll}
                  title="Select all on this page"
                />
              </th>
              <th style={thStyle}>ID</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>GHIN</th>
              <th style={thStyle}>VIP Card #</th>
              <th style={thStyle}>Bag Tag Name</th>
              <th style={thStyle}>Date</th>
              {showShipped && <th style={thStyle}>Ship Date</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={showShipped ? 9 : 8} style={{ ...tdStyle, textAlign: "center", color: "#6c757d", padding: "2rem" }}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && paginated.length === 0 && (
              <tr>
                <td colSpan={showShipped ? 9 : 8} style={{ ...tdStyle, textAlign: "center", color: "#6c757d", padding: "2rem" }}>
                  No bag tag purchases found.
                </td>
              </tr>
            )}
            {!loading && paginated.map((t, i) => {
              const isSelected = selected.has(t.dealId);
              return (
                <tr
                  key={t.dealId}
                  style={{
                    backgroundColor: isSelected ? "rgba(2,56,34,0.06)" : i % 2 === 0 ? "#fff" : "#fafafa",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleOne(t.dealId)}
                >
                  <td style={{ ...tdStyle, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOne(t.dealId)}
                    />
                  </td>
                  <td style={{ ...tdStyle }} onClick={(e) => e.stopPropagation()}>
                    <a
                      href={hubspotRecordUrl("deal", t.dealId)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-decoration-none"
                      style={{ color: BRAND_GREEN, fontWeight: 600 }}
                    >
                      {(safePage - 1) * pageSize + i + 1}
                    </a>
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>
                    {t.contactId ? (
                      <a
                        href={hubspotRecordUrl("contact", t.contactId)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-decoration-none"
                        style={{ color: BRAND_GREEN, fontWeight: 500 }}
                      >
                        {t.firstName || t.lastName ? `${t.firstName} ${t.lastName}`.trim() : "—"}
                      </a>
                    ) : (
                      t.firstName || t.lastName ? `${t.firstName} ${t.lastName}`.trim() : "—"
                    )}
                    {t.shippedDate && (
                      <span title={`Shipped ${formatDate(t.shippedDate)}`} style={{ marginLeft: "0.4rem", fontSize: "0.75rem", color: "#198754", fontWeight: 400 }}>
                        ✓ Shipped
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color: "#495057" }}>{t.email || "—"}</td>
                  <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: "0.82rem" }}>{t.ghinNumber || "—"}</td>
                  <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: "0.82rem" }}>{t.vipCardNumber || "—"}</td>
                  <td style={{ ...tdStyle }}>
                    {t.bagTagName
                      ? <strong style={{ color: BRAND_GREEN }}>{t.bagTagName}</strong>
                      : <span style={{ color: "#adb5bd" }}>—</span>
                    }
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap", color: "#6c757d", fontSize: "0.8rem" }}>
                    {formatDate(t.createDate)}
                  </td>
                  {showShipped && (
                    <td style={{ ...tdStyle }} onClick={(e) => e.stopPropagation()}>
                      {openPickerFor === t.dealId ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                          <input
                            type="date"
                            value={pickDate}
                            onChange={(e) => setPickDate(e.target.value)}
                            style={{ fontSize: "0.78rem", padding: "0.2rem 0.4rem", borderRadius: 4, border: "1px solid #ced4da", outline: "none", width: "100%" }}
                            autoFocus
                          />
                          <div style={{ display: "flex", gap: "0.3rem" }}>
                            <button
                              onClick={async () => {
                                if (!pickDate) return;
                                await handleShipCard(t, pickDate);
                                setOpenPickerFor(null);
                              }}
                              disabled={!pickDate || shipping2}
                              style={{ background: BRAND_GREEN, color: "#fff", border: "none", borderRadius: 4, padding: "0.2rem 0.6rem", fontSize: "0.75rem", fontWeight: 600, cursor: pickDate ? "pointer" : "default" }}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setOpenPickerFor(null)}
                              style={{ background: "none", border: "none", color: "#6c757d", cursor: "pointer", fontSize: "0.8rem", padding: "0.1rem 0.25rem" }}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : t.shippedCardDate ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                          <button
                            onClick={() => {
                              const existing = t.shippedCardDate
                                ? (() => { const d = new Date(Number(t.shippedCardDate)); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })()
                                : "";
                              setOpenPickerFor(t.dealId);
                              setPickDate(existing);
                            }}
                            title="Edit ship date"
                            style={{ background: "none", border: "none", color: "#198754", fontSize: "0.8rem", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: "0.3rem" }}
                          >
                            ✓ {formatDate(t.shippedCardDate)}
                            <i className="bi bi-pencil-fill" style={{ fontSize: "0.7rem", color: "#6c757d" }} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setOpenPickerFor(t.dealId); setPickDate(new Date().toISOString().split("T")[0]); }}
                          title="Set ship date"
                          style={{ background: "none", border: "1px dashed #ced4da", borderRadius: 5, color: "#6c757d", cursor: "pointer", padding: "0.2rem 0.5rem", fontSize: "0.78rem", display: "flex", alignItems: "center", gap: "0.3rem" }}
                        >
                          <i className="bi bi-box-seam" /> Set Ship Date
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", marginTop: "1rem", gap: "0.5rem" }}>
        <div />

        {totalPages > 1 && (
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", justifyContent: "center" }}>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={safePage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              ‹ Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
              .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("…");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === "…" ? (
                  <span key={`ellipsis-${i}`} style={{ padding: "0.25rem 0.5rem", color: "#6c757d" }}>…</span>
                ) : (
                  <button
                    key={p}
                    className="btn btn-sm"
                    onClick={() => setCurrentPage(p as number)}
                    style={{
                      backgroundColor: safePage === p ? BRAND_GREEN : "transparent",
                      color: safePage === p ? "#fff" : "#495057",
                      border: `1px solid ${safePage === p ? BRAND_GREEN : "#dee2e6"}`,
                      minWidth: "2.2rem",
                    }}
                  >
                    {p}
                  </button>
                )
              )}
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={safePage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next ›
            </button>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "0.5rem" }}>
          <span style={{ fontSize: "0.82rem", color: "#6c757d", whiteSpace: "nowrap" }}>Rows per page</span>
          <select
            className="form-select form-select-sm"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value) as 20 | 50 | 100)}
            style={{ width: "5rem" }}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default BulkBagTags;
